import type { AssignmentSegment, NormalizedRoundSchedule, RoundName } from "../../domain/round/types";
import type { RoundStaffSummary } from "../../lib/api";

export type ManualBuilderGroup = {
  id: string;
  number: number;
  tee: 1 | 10;
  startMinutes: number;
  playerCount: 2 | 3;
  playerNames: string[];
  tv: boolean;
};

export type ManualPlacement = {
  id: string;
  personId: string;
  groupId: string;
  segment: AssignmentSegment;
};

export type ManualCarBlock = {
  id: string;
  name: string;
  personIds: string[];
};

export type GridConfig = {
  groupCount: number;
  tee1Count: number;
  tee10Count: number;
  tee1FirstMinutes: number;
  tee10FirstMinutes: number;
  intervalMinutes: number;
  playerCount: 2 | 3;
};

export type DistributionMode = "both" | "tee1" | "tee10";

function uid(prefix: string) {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return `${prefix}-${crypto.randomUUID()}`;
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function clockToMinutes(value: string, fallback = 450) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(value || "").trim());
  if (!match) return fallback;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isInteger(hour) || !Number.isInteger(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) return fallback;
  return hour * 60 + minute;
}

export function minutesToClock(value: number) {
  const normalized = ((Math.round(value) % 1440) + 1440) % 1440;
  return `${String(Math.floor(normalized / 60)).padStart(2, "0")}:${String(normalized % 60).padStart(2, "0")}`;
}

export function groupNineMinutes(group: Pick<ManualBuilderGroup, "playerCount">) {
  return group.playerCount === 2 ? 120 : 150;
}

export function groupFinishMinutes(group: Pick<ManualBuilderGroup, "startMinutes" | "playerCount">) {
  return group.startMinutes + (group.playerCount === 2 ? 240 : 300);
}

export function createGrid(config: GridConfig): ManualBuilderGroup[] {
  const total = Math.max(1, Math.min(120, Math.round(config.groupCount || 1)));
  const tee1Count = Math.max(0, Math.min(total, Math.round(config.tee1Count || 0)));
  const tee10Count = Math.max(0, Math.min(total - tee1Count, Math.round(config.tee10Count || 0)));
  const missing = total - tee1Count - tee10Count;
  const effectiveTee1 = tee1Count + missing;
  const interval = Math.max(1, Math.min(60, Math.round(config.intervalMinutes || 10)));
  const output: ManualBuilderGroup[] = [];
  let number = 1;

  for (let index = 0; index < effectiveTee1; index += 1) {
    output.push({
      id: uid("manual-group"), number: number++, tee: 1,
      startMinutes: config.tee1FirstMinutes + index * interval,
      playerCount: config.playerCount, playerNames: [], tv: false,
    });
  }
  for (let index = 0; index < tee10Count; index += 1) {
    output.push({
      id: uid("manual-group"), number: number++, tee: 10,
      startMinutes: config.tee10FirstMinutes + index * interval,
      playerCount: config.playerCount, playerNames: [], tv: false,
    });
  }
  return output.sort((a, b) => a.number - b.number);
}

function isFullSegment(segment: AssignmentSegment) {
  return segment === "full18" || segment === "extraFull18";
}

function isFirstSegment(segment: AssignmentSegment) {
  return isFullSegment(segment) || segment === "holes1to9" || segment === "extra1to9";
}

function isSecondSegment(segment: AssignmentSegment) {
  return isFullSegment(segment) || segment === "holes10to18" || segment === "extra10to18";
}

export function coveredGroupIds(groups: ManualBuilderGroup[], placements: ManualPlacement[]) {
  const byGroup = new Map<string, ManualPlacement[]>();
  for (const item of placements) byGroup.set(item.groupId, [...(byGroup.get(item.groupId) || []), item]);
  const covered = new Set<string>();
  for (const group of groups) {
    const rows = byGroup.get(group.id) || [];
    if (rows.some((item) => isFirstSegment(item.segment)) && rows.some((item) => isSecondSegment(item.segment))) covered.add(group.id);
  }
  return covered;
}

function freeGroups(groups: ManualBuilderGroup[], placements: ManualPlacement[], ignoredPeople: Set<string>) {
  const occupied = new Set(
    placements
      .filter((item) => isFullSegment(item.segment) && !ignoredPeople.has(item.personId))
      .map((item) => item.groupId),
  );
  return groups.filter((group) => !occupied.has(group.id));
}

function consecutiveWindows<T>(values: T[], size: number) {
  if (size <= 0) return [[] as T[]];
  if (values.length < size) return [] as T[][];
  const result: T[][] = [];
  for (let index = 0; index <= values.length - size; index += 1) result.push(values.slice(index, index + size));
  return result;
}

function windowScore(groups: ManualBuilderGroup[]) {
  if (!groups.length) return [0, 0, 0] as const;
  const finishes = groups.map(groupFinishMinutes);
  const starts = groups.map((group) => group.startMinutes);
  return [Math.max(...finishes) - Math.min(...finishes), Math.max(...starts) - Math.min(...starts), Math.min(...starts)] as const;
}

function compareScore(a: readonly number[], b: readonly number[]) {
  for (let index = 0; index < Math.max(a.length, b.length); index += 1) {
    const left = a[index] ?? 0;
    const right = b[index] ?? 0;
    if (left !== right) return left - right;
  }
  return 0;
}

function bestSingleTeeWindow(candidates: ManualBuilderGroup[], size: number) {
  const sorted = [...candidates].sort((a, b) => a.startMinutes - b.startMinutes || a.number - b.number);
  const windows = consecutiveWindows(sorted, size);
  return windows.sort((a, b) => compareScore(windowScore(a), windowScore(b)))[0] || [];
}

function bestBalancedWindow(candidates: ManualBuilderGroup[], size: number) {
  const tee1 = candidates.filter((group) => group.tee === 1).sort((a, b) => a.startMinutes - b.startMinutes || a.number - b.number);
  const tee10 = candidates.filter((group) => group.tee === 10).sort((a, b) => a.startMinutes - b.startMinutes || a.number - b.number);
  const splits = size % 2 === 0
    ? [[size / 2, size / 2]]
    : [[Math.ceil(size / 2), Math.floor(size / 2)], [Math.floor(size / 2), Math.ceil(size / 2)]];
  let best: ManualBuilderGroup[] = [];
  let bestScore: readonly number[] | null = null;

  for (const [count1, count10] of splits) {
    for (const left of consecutiveWindows(tee1, count1)) {
      for (const right of consecutiveWindows(tee10, count10)) {
        const combined = [...left, ...right];
        const score = windowScore(combined);
        if (!bestScore || compareScore(score, bestScore) < 0) {
          best = combined;
          bestScore = score;
        }
      }
    }
  }
  return best;
}

export function distributeCarBlock(
  groups: ManualBuilderGroup[],
  placements: ManualPlacement[],
  personIds: string[],
  mode: DistributionMode,
  anchorGroupId?: string,
) {
  const people = [...new Set(personIds.filter(Boolean))];
  if (!people.length) return { placements, assignedGroupIds: [] as string[], error: "Blok nie ma żadnych osób." };
  const ignored = new Set(people);
  const basePlacements = placements.filter((item) => !(ignored.has(item.personId) && item.segment === "full18"));
  const free = freeGroups(groups, basePlacements, ignored);
  let candidates = mode === "tee1" ? free.filter((group) => group.tee === 1) : mode === "tee10" ? free.filter((group) => group.tee === 10) : free;
  const anchor = anchorGroupId ? groups.find((group) => group.id === anchorGroupId) : undefined;
  if (anchor && mode !== "both") candidates = candidates.filter((group) => group.startMinutes >= anchor.startMinutes);
  if (candidates.length < people.length) {
    return { placements, assignedGroupIds: [] as string[], error: `Brakuje wolnych grup: blok ma ${people.length} osób, a dostępnych jest ${candidates.length}.` };
  }

  let selected: ManualBuilderGroup[] = [];
  if (mode === "both") selected = bestBalancedWindow(candidates, people.length);
  else selected = bestSingleTeeWindow(candidates, people.length);
  if (selected.length !== people.length) {
    return { placements, assignedGroupIds: [] as string[], error: mode === "both" ? "Nie da się rozłożyć bloku równo pomiędzy Tee 1 i Tee 10." : "Nie znaleziono kolejnych wolnych grup na tym tee." };
  }

  selected.sort((a, b) => groupFinishMinutes(a) - groupFinishMinutes(b) || a.startMinutes - b.startMinutes || a.number - b.number);
  const added = people.map((personId, index) => ({
    id: uid("manual-assignment"), personId, groupId: selected[index].id, segment: "full18" as const,
  }));
  return { placements: [...basePlacements, ...added], assignedGroupIds: selected.map((group) => group.id), error: "" };
}

function segmentInterval(group: ManualBuilderGroup, segment: AssignmentSegment) {
  const nine = groupNineMinutes(group);
  const finish18 = groupFinishMinutes(group);
  if (segment === "holes1to9" || segment === "extra1to9") return [group.startMinutes, group.startMinutes + nine] as const;
  if (segment === "holes10to18" || segment === "extra10to18") return [group.startMinutes + nine, finish18] as const;
  return [group.startMinutes, finish18] as const;
}

function segmentHoles(segment: AssignmentSegment) {
  return isFullSegment(segment) ? 18 : 9;
}

function weekdayWaveSplit(groups: Array<{ startMinutes: number }>) {
  const starts = [...new Set(groups.map((group) => group.startMinutes))].sort((a, b) => a - b);
  let split = 720;
  let largestGap = 0;
  for (let index = 0; index < starts.length - 1; index += 1) {
    const gap = starts[index + 1] - starts[index];
    if (gap >= 75 && gap > largestGap) {
      largestGap = gap;
      split = starts[index + 1];
    }
  }
  return split;
}

export function buildSchedule(
  tournamentId: string,
  round: RoundName,
  groups: ManualBuilderGroup[],
  placements: ManualPlacement[],
  staff: RoundStaffSummary[],
): NormalizedRoundSchedule {
  const validGroupIds = new Set(groups.map((group) => group.id));
  const staffById = new Map(staff.map((person) => [person.personId, person]));
  const validPlacements = placements.filter((item) => validGroupIds.has(item.groupId) && staffById.has(item.personId));
  const placementsByPerson = new Map<string, ManualPlacement[]>();
  for (const item of validPlacements) placementsByPerson.set(item.personId, [...(placementsByPerson.get(item.personId) || []), item]);
  const groupById = new Map(groups.map((group) => [group.id, group]));

  const people = staff.map((person) => {
    const rows = placementsByPerson.get(person.personId) || [];
    const duties = rows.map((item) => {
      const group = groupById.get(item.groupId)!;
      const interval = segmentInterval(group, item.segment);
      return { ...item, start: interval[0], end: interval[1] };
    }).sort((a, b) => a.start - b.start || a.end - b.end);
    let longestBreak = 0;
    for (let index = 1; index < duties.length; index += 1) longestBreak = Math.max(longestBreak, duties[index].start - duties[index - 1].end);
    return {
      personId: person.personId,
      displayName: person.displayName,
      holes: rows.reduce((total, item) => total + segmentHoles(item.segment), 0),
      dutyStartMinutes: duties.length ? Math.min(...duties.map((item) => item.start)) : null,
      dutyEndMinutes: duties.length ? Math.max(...duties.map((item) => item.end)) : null,
      longestBreakMinutes: Math.max(0, longestBreak),
      assignmentIds: rows.map((item) => item.id),
      driverEligible: person.driverEligible,
      transportIncluded: person.transportIncluded,
    };
  });

  const normalizedGroups = [...groups]
    .sort((a, b) => a.number - b.number)
    .map((group) => ({
      id: group.id,
      number: group.number,
      tee: group.tee,
      startMinutes: group.startMinutes,
      finish9Minutes: group.startMinutes + groupNineMinutes(group),
      finish18Minutes: groupFinishMinutes(group),
      playerNames: group.playerNames,
      tv: group.tv,
      requiredScorers: 1,
      coverageScorers: 1,
    }));

  const waveSplit = weekdayWaveSplit(normalizedGroups);
  const sessionModel: NormalizedRoundSchedule["sessionModel"] = round === "R1" || round === "R2"
    ? {
        kind: "weekday-two-session",
        sessions: [
          { id: "morning", label: "Morning", groupIds: normalizedGroups.filter((group) => group.startMinutes < waveSplit).map((group) => group.id) },
          { id: "afternoon", label: "Afternoon", groupIds: normalizedGroups.filter((group) => group.startMinutes >= waveSplit).map((group) => group.id) },
        ],
      }
    : { kind: "weekend-single-session", session: { id: "round", label: round, groupIds: normalizedGroups.map((group) => group.id) } };

  return {
    tournamentId,
    round,
    groups: normalizedGroups,
    assignments: validPlacements.map((item) => ({ ...item, source: "manual", locked: false })),
    people,
    sessionModel,
    preferredBreakMinutes: 40,
    emergencyBreakMinutes: 20,
  };
}

export function manualBuilderProblems(groups: ManualBuilderGroup[], placements: ManualPlacement[], staff: RoundStaffSummary[]) {
  const problems: string[] = [];
  if (!groups.length) problems.push("Dodaj co najmniej jedną grupę.");
  const numbers = groups.map((group) => group.number);
  if (new Set(numbers).size !== numbers.length) problems.push("Numery grup muszą być unikalne.");
  if (groups.some((group) => !Number.isFinite(group.startMinutes) || group.startMinutes < 0 || group.startMinutes >= 1440)) problems.push("Każda grupa musi mieć poprawną godzinę startu.");
  const activeIds = new Set(staff.map((person) => person.personId));
  if (placements.some((item) => !activeIds.has(item.personId))) problems.push("Plan zawiera osobę spoza aktualnej obsady rundy.");
  const covered = coveredGroupIds(groups, placements);
  const uncovered = groups.filter((group) => !covered.has(group.id));
  if (uncovered.length) problems.push(`${uncovered.length} grup nie ma jeszcze obsady na całe 18 dołków.`);
  return problems;
}

export function importSchedule(schedule: NormalizedRoundSchedule | null | undefined) {
  if (!schedule) return { groups: [] as ManualBuilderGroup[], placements: [] as ManualPlacement[] };
  return {
    groups: schedule.groups.map((group) => {
      const players = Array.isArray(group.playerNames) ? group.playerNames : [];
      const duration = Number(group.finish18Minutes ?? 0) - Number(group.startMinutes ?? 0);
      return {
        id: group.id,
        number: group.number,
        tee: group.tee === 10 ? 10 as const : 1 as const,
        startMinutes: group.startMinutes,
        playerCount: duration > 0 && duration <= 250 ? 2 as const : 3 as const,
        playerNames: players,
        tv: Boolean(group.tv),
      };
    }),
    placements: schedule.assignments.map((item) => ({ id: item.id, personId: item.personId, groupId: item.groupId, segment: item.segment })),
  };
}
